import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { ReactNode, useEffect, useState } from 'react';
import { SortableHeaderIcon, PaginationControls, paginationPropsFromTable } from './table-shared';

interface DataTableProps<T extends object> {
  columns: ColumnDef<T, any>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  pageSizeOptions?: number[];
  filters?: ReactNode;
}

export function DataTable<T extends object>({
  columns,
  data,
  loading,
  emptyMessage = 'Sin datos',
  pageSizeOptions = [5, 10, 20, 50],
  filters
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  useEffect(() => {
    table.setPageSize(pageSize);
  }, [pageSize, table]);

  return (
    <div className="space-y-3">
      {filters && <div>{filters}</div>}
      <div className="overflow-x-auto card">
        <table className="table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <span className="inline-flex items-center gap-1">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <SortableHeaderIcon sortDirection={header.column.getIsSorted()} />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-3 py-3 text-sm text-slate-600" colSpan={columns.length}>
                  Cargando...
                </td>
              </tr>
            )}
            {!loading && table.getRowModel().rows.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-sm text-slate-600" colSpan={columns.length}>
                  {emptyMessage}
                </td>
              </tr>
            )}
            {!loading && table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls {...paginationPropsFromTable(table, pageSize, pageSizeOptions, setPageSize)} />
    </div>
  );
}

